import React from 'react';

import { Theme } from '@material-ui/core';
import { InputProps as StandardInputProps } from '@material-ui/core/Input';
import { InputLabelProps } from '@material-ui/core/InputLabel';
import { createStyles, withStyles } from '@material-ui/core/styles';
import MuiTextField, { StandardTextFieldProps } from '@material-ui/core/TextField';
import classnames from 'classnames';
import { get, omit } from 'lodash';

import MaskedInputComponent from './MaskedInputComponent';

const styles = ({ palette: { error } }: Theme) =>
  createStyles({
    asterisk: {
      color: error.main,
    },
    capitalize: {
      textTransform: 'capitalize',
    },
    inputSmall: {
      height: '35px !important',
    },
  });

export interface TextFieldProps extends Omit<StandardTextFieldProps, 'variant'> {
  classes: any;
  capitalizeFirstLetter?: boolean;
  floatingLabel?: boolean;
  mask?: string | Array<string | RegExp>;
  maskPlaceholder?: string;
  maxLength?: number;
  size?: 'default' | 'small';
  showRequiredMark?: boolean;
}

const TextField: React.FC<TextFieldProps> = ({
  capitalizeFirstLetter,
  classes,
  floatingLabel,
  inputRef,
  label,
  mask,
  maskPlaceholder,
  maxLength,
  showRequiredMark,
  size = 'default',
  ...other
}) => {
  let InputProps: Partial<StandardInputProps> = get(other, 'InputProps', {});
  let inputProps: StandardInputProps['inputProps'] = get(InputProps, 'inputProps', {});
  const inputClasses = get(InputProps, 'classes', {});

  InputProps = {
    ...InputProps,
    classes: {
      ...inputClasses,
      input: classnames(get(inputClasses, 'input', ''), {
        [classes.inputSmall]: size === 'small',
        [classes.capitalize]: capitalizeFirstLetter,
      }),
    },
  };

  if (mask) {
    InputProps.inputComponent = MaskedInputComponent;
    inputProps = {
      ...inputProps,
      mask,
      maskPlaceholder,
    };
  } else {
    inputProps.maxLength = maxLength;
  }

  const inputComponentProps = { ...omit(InputProps, ['inputProps']), inputProps: { ...inputProps } };
  const inputComponentLabelProps: Partial<InputLabelProps> = get(other, 'InputLabelProps', {});
  let fieldLabel: React.ReactNode = label;

  if (showRequiredMark && label) {
    fieldLabel = (
      <div>
        {label}
        <span className={classes.asterisk}>*</span>
      </div>
    );
  }

  if (floatingLabel !== undefined) {
    inputComponentLabelProps.shrink = floatingLabel;
  }

  return (
    <MuiTextField
      InputLabelProps={{ ...inputComponentLabelProps }}
      InputProps={{ ...inputComponentProps }}
      fullWidth
      inputRef={inputRef}
      label={fieldLabel}
      {...omit(other, ['InputProps'])}
    />
  );
};

export default withStyles(styles)(TextField);
